import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Check, Heart, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatApiError, photoUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

function useCountdown(endsAt) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    if (!endsAt) return null;
    const ms = new Date(endsAt).getTime() - now;
    if (ms <= 0) return "Voting has ended";
    const s = Math.floor(ms / 1000);
    const days = Math.floor(s / 86400);
    const hrs = Math.floor((s % 86400) / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    if (days > 0) return `${days}d ${hrs}h ${mins}m`;
    if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
    return `${mins}m ${secs}s`;
}

export default function Vote() {
    const { user } = useAuth();
    const [data, setData] = useState({ finalists: [], voting_open: false, voting_ends_at: null });
    const [myVote, setMyVote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [voting, setVoting] = useState(null); // submission id being submitted
    const [openCard, setOpenCard] = useState(null);

    const countdown = useCountdown(data.voting_ends_at);

    const fetchAll = async () => {
        try {
            const [r1, r2] = await Promise.all([api.get("/finalists"), api.get("/votes/me")]);
            setData(r1.data);
            setMyVote(r2.data.submission_id);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to load finalists"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const castVote = async (id) => {
        if (myVote || voting) return;
        if (!data.voting_open) {
            toast.error("Voting isn't open yet.");
            return;
        }
        setVoting(id);
        try {
            await api.post("/votes", { submission_id: id });
            setMyVote(id);
            toast.success("Your vote is in. Thank you.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setVoting(null);
        }
    };

    const finalists = data.finalists;

    const hero = useMemo(
        () => ({
            count: finalists.length,
            voted: !!myVote,
        }),
        [finalists, myVote]
    );

    return (
        <div className="App min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="vote-section">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <div>
                            <div className="label-overline">Vote · {hero.count} finalists</div>
                            <h1 className="font-serif text-4xl sm:text-5xl text-tmc-ink mt-3 leading-tight">
                                Choose the moment that moved you most.
                            </h1>
                            <p className="text-tmc-inkSoft mt-3 max-w-xl">
                                One vote per teammate. Names are kept private — vote on the story, not the storyteller.
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 items-start lg:items-end">
                            <div
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                                    data.voting_open
                                        ? "bg-tmc-moss/15 text-tmc-mossDark"
                                        : "bg-tmc-sand text-tmc-inkSoft"
                                }`}
                                data-testid="voting-status"
                            >
                                <span
                                    className={`w-2 h-2 rounded-full ${
                                        data.voting_open ? "bg-tmc-moss animate-soft-pulse" : "bg-tmc-muted"
                                    }`}
                                />
                                {data.voting_open ? "Voting open" : "Voting closed"}
                            </div>
                            {countdown && (
                                <div className="text-sm text-tmc-muted inline-flex items-center gap-1.5" data-testid="countdown">
                                    <Clock className="w-3.5 h-3.5" /> {countdown}
                                </div>
                            )}
                            {hero.voted && (
                                <div className="text-sm text-tmc-terracotta font-medium inline-flex items-center gap-1.5" data-testid="vote-confirmed">
                                    <Check className="w-4 h-4" /> Your vote is recorded
                                </div>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div className="py-24 flex items-center justify-center text-tmc-muted">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading finalists…
                        </div>
                    ) : finalists.length === 0 ? (
                        <div className="py-24 text-center">
                            <div className="font-serif text-2xl text-tmc-ink">Finalists are still being curated.</div>
                            <p className="text-tmc-muted mt-2">Check back soon — they'll appear here.</p>
                        </div>
                    ) : (
                        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="finalists-grid">
                            {finalists.map((f, i) => {
                                const isMyVote = myVote === f.id;
                                const disabled = !!myVote || !data.voting_open;
                                return (
                                    <article
                                        key={f.id}
                                        className={`group tmc-card overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${
                                            isMyVote ? "ring-2 ring-tmc-terracotta" : ""
                                        }`}
                                        data-testid={`finalist-card-${i}`}
                                    >
                                        <div className="relative aspect-[4/3] overflow-hidden bg-tmc-sand">
                                            {f.photo_url && (
                                                <img
                                                    src={photoUrl(f.photo_url)}
                                                    alt=""
                                                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                                />
                                            )}
                                            {f.department && (
                                                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-xs font-medium text-tmc-inkSoft">
                                                    {f.department}
                                                </div>
                                            )}
                                            {isMyVote && (
                                                <div className="absolute top-3 right-3 bg-tmc-terracotta text-white px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                                                    <Heart className="w-3 h-3 fill-white" /> Your vote
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-6 flex-1 flex flex-col">
                                            <p className={`text-tmc-inkSoft leading-relaxed text-base ${openCard === f.id ? "" : "line-clamp-5"}`}>
                                                {f.description}
                                            </p>
                                            {f.description && f.description.length > 200 && (
                                                <button
                                                    onClick={() => setOpenCard(openCard === f.id ? null : f.id)}
                                                    className="mt-2 text-sm text-tmc-terracotta hover:underline self-start"
                                                    data-testid={`expand-${i}`}
                                                >
                                                    {openCard === f.id ? "Show less" : "Read more"}
                                                </button>
                                            )}
                                            <div className="mt-6 pt-6 border-t border-tmc-line flex items-center justify-between gap-3">
                                                <button
                                                    onClick={() => castVote(f.id)}
                                                    disabled={disabled || voting === f.id}
                                                    className={`flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all ${
                                                        isMyVote
                                                            ? "bg-tmc-terracotta text-white"
                                                            : disabled
                                                            ? "bg-tmc-sand text-tmc-muted cursor-not-allowed"
                                                            : "bg-tmc-ink text-white hover:bg-tmc-terracotta"
                                                    }`}
                                                    data-testid={`vote-btn-${i}`}
                                                >
                                                    {voting === f.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : isMyVote ? (
                                                        <>
                                                            <Check className="w-4 h-4" /> Voted
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Heart className="w-4 h-4" /> Vote for this
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}

                    {user && (
                        <p className="text-xs text-tmc-muted mt-12 text-center" data-testid="vote-policy">
                            Signed in as {user.email}. Each teammate may cast one vote total.
                        </p>
                    )}
                </section>
            </main>
            <Footer />
        </div>
    );
}
